/**
 * Auth Service
 *
 * Handles all authentication-related API calls.
 * Uses the FastAPI-Users endpoints from Sentinel.
 */
import axios from 'axios'
import type {
  User,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
} from '../types/auth'
import { apiHelpers } from './api'

// Auth endpoints use the same base but different path structure
const AUTH_BASE = import.meta.env.DEV ? '/api/v1/auth' : 'https://api.claimshieldpro.com/api/v1/auth'

/**
 * Auth service for authentication operations
 */
export const authService = {
  /**
   * Login with email/password
   * FastAPI-Users expects form-urlencoded data for OAuth2 password flow
   */
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    // FastAPI-Users OAuth2 expects form data, not JSON
    const formData = new URLSearchParams()
    formData.append('username', credentials.username)
    formData.append('password', credentials.password)

    const response = await axios.post<LoginResponse>(`${AUTH_BASE}/login`, formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    })

    return response.data
  },

  /**
   * Register a new user
   */
  async register(data: RegisterRequest): Promise<User> {
    const response = await axios.post<User>(`${AUTH_BASE}/register`, data)
    return response.data
  },

  /**
   * Get current authenticated user
   */
  async getCurrentUser(): Promise<User> {
    return apiHelpers.get<User>('/v1/users/me')
  },

  /**
   * Update current user profile
   */
  async updateProfile(data: Partial<User>): Promise<User> {
    return apiHelpers.patch<User>('/v1/users/me', data)
  },

  /**
   * Request password reset email
   */
  async forgotPassword(data: ForgotPasswordRequest): Promise<void> {
    await axios.post(`${AUTH_BASE}/forgot-password`, data)
  },

  /**
   * Reset password with token
   */
  async resetPassword(data: ResetPasswordRequest): Promise<void> {
    await axios.post(`${AUTH_BASE}/reset-password`, data)
  },

  /**
   * Verify email with token
   */
  async verifyEmail(token: string): Promise<void> {
    await axios.post(`${AUTH_BASE}/verify`, { token })
  },

  /**
   * Request new verification email
   */
  async requestVerification(email: string): Promise<void> {
    await axios.post(`${AUTH_BASE}/request-verify-token`, { email })
  },

  /**
   * Logout (optional - mainly client-side)
   * FastAPI-Users JWT doesn't have server-side logout by default
   */
  async logout(): Promise<void> {
    // If your backend has a logout endpoint, call it here
    // await axios.post(`${AUTH_BASE}/logout`)

    // For JWT, logout is primarily client-side (clear tokens)
    return Promise.resolve()
  },
}
